<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import McpServerCard from 'dashboard/components-next/captain/pageComponents/mcp/McpServerCard.vue';
import McpServerFormDialog from 'dashboard/components-next/captain/pageComponents/mcp/McpServerFormDialog.vue';
import DeleteDialog from 'dashboard/components-next/captain/pageComponents/DeleteDialog.vue';

const { t } = useI18n();
const store = useStore();

const uiFlags = useMapGetter('captainMcpServers/getUIFlags');
const servers = useMapGetter('captainMcpServers/getRecords');
const meta = useMapGetter('captainMcpServers/getMeta');

const isFetching = computed(() => uiFlags.value.fetchingList);

const formDialogRef = ref(null);
const deleteDialogRef = ref(null);
const selectedServer = ref(null);
// Tracks whether the form dialog should be mounted. Used as `v-if` so
// the form is fully torn down on close — otherwise reopening "Add MCP
// Server" after a previous save would surface the stale form state from
// the prior session (the watcher on props.server doesn't fire when
// selectedServer goes from null -> null). Same pattern the tools page
// uses with its dialogType flag.
const isFormOpen = ref(false);
// Per-row refresh-in-flight tracking. uiFlags.updatingItem is shared
// across all rows; this lets each card show its own spinner without
// blocking the others.
const refreshingIds = ref(new Set());

const fetchServers = (page = 1) => {
  store.dispatch('captainMcpServers/get', { page });
};

const openCreateDialog = () => {
  selectedServer.value = null;
  isFormOpen.value = true;
  nextTick(() => formDialogRef.value?.dialogRef?.open());
};

const handleEdit = server => {
  selectedServer.value = server;
  isFormOpen.value = true;
  nextTick(() => formDialogRef.value?.dialogRef?.open());
};

const handleFormClose = () => {
  isFormOpen.value = false;
  selectedServer.value = null;
};

const handleDelete = server => {
  selectedServer.value = server;
  nextTick(() => deleteDialogRef.value?.dialogRef?.open());
};

const handleRefresh = async id => {
  refreshingIds.value.add(id);
  try {
    const updated = await store.dispatch('captainMcpServers/refresh', id);
    if (updated?.discovery_error) {
      useAlert(t('CAPTAIN.MCP_SERVERS.REFRESH_FAILED'));
    } else {
      useAlert(t('CAPTAIN.MCP_SERVERS.REFRESH_SUCCESS'));
    }
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.MCP_SERVERS.REFRESH_FAILED'));
  } finally {
    refreshingIds.value.delete(id);
  }
};

const handleSaved = () => {
  isFormOpen.value = false;
  selectedServer.value = null;
  fetchServers(meta.value.page || 1);
};

const onDeleteSuccess = () => {
  selectedServer.value = null;
  fetchServers(meta.value.page || 1);
};

onMounted(fetchServers);
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.MCP_SERVERS.HEADER')"
    :button-label="t('CAPTAIN.MCP_SERVERS.ADD_NEW')"
    :button-policy="['administrator']"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    :total-count="meta.totalCount"
    :current-page="meta.page"
    :show-pagination-footer="!isFetching && !!servers.length"
    :is-fetching="isFetching"
    :is-empty="!servers.length"
    :show-know-more="false"
    @update:current-page="fetchServers"
    @click="openCreateDialog"
  >
    <template #emptyState>
      <div class="flex flex-col items-center justify-center gap-3 py-16">
        <div class="i-lucide-plug-zap size-10 text-n-slate-9" />
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CAPTAIN.MCP_SERVERS.EMPTY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-md">
          {{ t('CAPTAIN.MCP_SERVERS.EMPTY.SUBTITLE') }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-3">
        <McpServerCard
          v-for="server in servers"
          :key="server.id"
          :server="server"
          :is-refreshing="refreshingIds.has(server.id)"
          @refresh="handleRefresh"
          @edit="handleEdit"
          @delete="handleDelete"
        />
      </div>
    </template>
  </PageLayout>

  <McpServerFormDialog
    v-if="isFormOpen"
    ref="formDialogRef"
    :server="selectedServer"
    @close="handleFormClose"
    @saved="handleSaved"
  />

  <DeleteDialog
    v-if="selectedServer"
    ref="deleteDialogRef"
    :entity="selectedServer"
    type="McpServers"
    translation-key="MCP_SERVERS"
    @delete-success="onDeleteSuccess"
  />
</template>
